<script lang="ts">
    import {isWeekend, isToday} from "date-fns";
    import type {GetOccupantTypeCountByBucketQuery} from "../../frontendGatewaySdk";

    import TitledDisplay from "@cohesionib/biblioteca/lib/TitledDisplay";
    import LinearPortionChart from "@cohesionib/biblioteca/lib/LinearPortionChart";
    import Snapshot from "../../shared/SnapshotSection.svelte";
    import OccupancyKeycardTenantBreakdown from "./OccupancyKeycardTenantBreakdown.svelte";
    import OccupancyKeycardHourlyCheckIns from "./OccupancyKeycardHourlyCheckIns.svelte";
    import OccupancyKeycardMultipleHourlyCheckIns from "./OccupancyKeycardMultipleHourlyCheckIns.svelte";

    export let occupantTypeCountByBucketData: GetOccupantTypeCountByBucketQuery | null = null;
    export let includeWeekends: boolean = false;
    export let capacity: number = 0;
    export let rangeDays: number = 0;
    export let previousOccupancyValues: GetOccupantTypeCountByBucketQuery | null = null;
    export let pickerStartDate: Date | number | null = null;
    export let pickerEndDate: Date | null = null;

    let staffCount: number = 0
    let tenantCount: number = 0
    let visitorCount: number = 0

    const getFilteredBucket = (buckets) => {
        let filteredBucket = buckets;
        if (!includeWeekends) {
            filteredBucket = buckets.filter(bucket => !isWeekend(new Date(bucket.bucket)))
        }
        return filteredBucket;
    }

    const getTotalCheckins = (data: any, updateCheckinBreakdown = false): number => {

        const _staffCount = data?.occupantTypeCountByBucket?.reduce((sum:any, curr:any) => sum + (curr.staffCount || 0), 0) || 0;
        const _tenantCount = data?.occupantTypeCountByBucket?.reduce((sum:any, curr:any) => sum + (curr.tenantCount || 0), 0) || 0;
        const _visitorCount = data?.occupantTypeCountByBucket?.reduce((sum:any, curr:any) => sum + (curr.visitorCount || 0), 0) || 0;

        if (updateCheckinBreakdown) {
            staffCount = _staffCount;
            tenantCount = _tenantCount;
            visitorCount = _visitorCount;
        }
        return _staffCount + _tenantCount + _visitorCount;
    }

    $: data = occupantTypeCountByBucketData?.building || [];
    $: occupantTypeCountByBucket = data?.occupantTypeCountByBucket || [];
    $: buckets = getFilteredBucket(occupantTypeCountByBucket);
    $: totalCheckins = getTotalCheckins(data, true);
    $: averageCheckins = Math.round(totalCheckins / (buckets.length === 0 ? 1 : buckets.length));
    $: previousTotalCheckins = previousOccupancyValues ? getTotalCheckins(previousOccupancyValues.building) : 0;
    $: occupancyRate = capacity
        ? Math.round((averageCheckins / capacity) * 100) || 0
        : 0;
    $: occupancyRateChangeCalc = Math.round((totalCheckins - previousTotalCheckins) / previousTotalCheckins * 100);
    $: occupancyRateChange = isFinite(occupancyRateChangeCalc) ? occupancyRateChangeCalc : 0;
</script>
 
<style>
    div {
        font-weight: 300;
    }
    .snapshot-container {
        padding-top: 1em;
        width: 100%;
    }
    .chart-wrapper-section {
        display: flex;
    }
    .tenant-container {
        padding-top: 2em;
        width: 50%;
        padding-right: 1em;
    }
    .checkins-container {
        padding-top: 2em;
        width: 50%;
    }
    @media only screen and (max-width: 1350px) {
        .chart-wrapper-section {
            display: block;
        }
        .tenant-container {
            width: 100%;
        }
        .checkins-container {
            width: 100%;
        }
    }
    .positive-number {
        color: var(--theme-accent);
        font-size: 3em;
        line-height: min-content;
    }
    .negative-number {
        color: #BE6984;
        font-size: 3em;
        line-height: min-content;
    }
    .chart-wrapper {
        width: 12em;
    }
    .graph-content-wrapper {
        width: 100%;
        padding: 0 2em 1.5em 2em;
    }
    .graph-hourly-wrapper {
        height: 25rem;
    }
    .graph-subtitle {
        color: #4e4e4e;
        font-size: 0.75em;
        padding-bottom: 2.5em;
    }
    .snapshot-child-wrapper {
        margin: 0 4em 1.5em 4em;
        display: flex;
        justify-content: space-between;
        width: 100%;
    }
    .snapshot-child {
        min-width: 8em;
    }
    .center {
        text-align: center;
    }
</style>

<div class="occupancy-keycards">
    <div class="snapshot-container">
        <Snapshot title="Snapshot">
            <div class="snapshot-child-wrapper">
                <div class="snapshot-child center">
                    <TitledDisplay title="Check-Ins">
                        <div class="positive-number">{totalCheckins}</div>
                    </TitledDisplay>
                </div>
                <div class="snapshot-child center">
                    <TitledDisplay title="Occupancy Rate">
                        <div
                                class:positive-number={occupancyRate >= 0}
                                class:negative-number={occupancyRate < 0}
                        >{occupancyRate}%</div>
                    </TitledDisplay>
                </div>
                {#if rangeDays === 1 && !isToday(pickerStartDate)}
                    <div class="snapshot-child center">
                        <TitledDisplay title="Occupancy Rate Change">
                            <div
                                    class:positive-number={occupancyRateChange >= 0}
                                    class:negative-number={occupancyRateChange < 0}
                            >{isFinite(occupancyRateChangeCalc) ? `${occupancyRateChange}%` : '-'}</div>
                        </TitledDisplay>
                    </div>
                {/if}
                <div class="snapshot-child">
                    <TitledDisplay title="Check-In Breakdown">
                        {#if tenantCount + staffCount}
                            <div class="chart-wrapper">
                                <LinearPortionChart
                                        labels={['Occupants', 'Staff']}
                                        data={[tenantCount, staffCount]} />
                            </div>
                        {:else}
                            <div class="positive-number center">-</div>
                        {/if}
                    </TitledDisplay>
                </div>
            </div>
        </Snapshot>
    </div>
    <div class="chart-wrapper-section">
        <div class="tenant-container">
            <OccupancyKeycardTenantBreakdown {pickerStartDate} {pickerEndDate} {rangeDays} {includeWeekends}/>
        </div>
        <div class="checkins-container">
            <Snapshot title="Hourly Check-Ins">
                <div class="graph-content-wrapper graph-hourly-wrapper">
                    <div class="graph-subtitle">
                        When do occupants arrive during the day?
                    </div>
                        {#if rangeDays === 1}
                            <OccupancyKeycardHourlyCheckIns {pickerStartDate} {includeWeekends}/>
                        {:else}
                            <OccupancyKeycardMultipleHourlyCheckIns {pickerStartDate} {pickerEndDate} {includeWeekends}/>
                        {/if}
                </div>
            </Snapshot>
        </div>
    </div>
</div>
